<template>
    <div class="accounting-detail-data accounting-variable-statement-detail">
        <div class="detail-container">
            <div class="detail-section">
                <div class="accounting-detail-items" style="margin-top: 25px;">
                    <div class="item vertical">
                        <div class="label">摘要</div>
                        <div class="value xl b">{{ documentDetail.tpc }}</div>
                    </div>
                </div>
                <div class="accounting-detail-items" style="margin-top: 25px;">
                    <div class="item">
                        <div class="label">系統單號</div>
                        <div class="value">
                            <el-button type="text" @click="openVariableDetail(documentDetail.sno)">{{ documentDetail.moo }}</el-button>
                        </div>
                    </div>
                    <div class="item">
                        <div class="label">
                            <span v-if="isIncome">收入</span>
                            <span v-if="isExpense">支出</span>
                            <span>項目</span>
                        </div>
                        <div class="value">{{ getHierarchyItemNames(documentDetail.iteM_SNO) }}</div>
                    </div>
                    <div class="item">
                        <div class="label">備註</div>
                        <div class="value">{{ documentDetail.mem || "--" }}</div>
                    </div>
                </div>
            </div>
            <div class="detail-section">
                <div class="section-title">
                    <span v-if="isIncome">收入</span>
                    <span v-if="isExpense">支出</span>
                    <span>資訊</span>
                </div>
                <div class="accounting-detail-items" style="margin-top: 25px;">
                    <div class="item">
                        <div class="label">
                            <span v-if="isIncome">入帳</span>
                            <span v-if="isExpense">付款</span>
                            <span>日期</span>
                        </div>
                        <div class="value">{{ documentDetail.paY_DAT | dateFormater("YYYY-MM-dd") }}</div>
                    </div>
                    <div class="item">
                        <div class="label">
                            <span v-if="isIncome">入帳</span>
                            <span v-if="isExpense">付款</span>
                            <span>方式</span>
                        </div>
                        <div class="value">{{ documentDetail.bnK_SNO < 0 ? "零用現金付款" : "銀行轉帳" }}</div>
                    </div>
                    <div class="item">
                        <div class="label">
                            <span v-if="isIncome">入帳</span>
                            <span v-if="isExpense">付款</span>
                            <span>金額</span>
                        </div>
                        <div class="value xl">
                            <Money :v="documentDetail.amt" :precision="documentDetail.cny === 'TWD' ? null : 4" /><span class="cny">{{ documentDetail.cny }}</span>
                        </div>
                    </div>
                    <div class="item" v-if="documentDetail.cny !== 'TWD'">
                        <div class="label">匯率</div>
                        <div class="value">{{ documentDetail.rat }} （約當台幣：<Money :v="documentDetail.amt * documentDetail.rat" round /> 元）</div>
                    </div>
                    <div class="item">
                        <div class="label">
                            <span v-if="isIncome">入帳</span>
                            <span v-if="isExpense">付款</span>
                            <span>帳戶</span>
                        </div>
                        <div class="value">{{ getBankName(documentDetail.bnK_SNO) }}</div>
                    </div>
                </div>
            </div>
            <div class="detail-section" v-for="(file, index) in documentDetail.attachments" :key="index">
                <div class="section-title">檢附文件 #{{ index + 1 }}</div>
                <div class="split-view">
                    <div class="attachment-rect">
                        <div v-if="['jpeg', 'jpg', 'png'].includes(getFileExtByUrl(file.attachmentUrl))" style="width: 120px; height: 158px;">
                            <el-image style="width: 120px; height: 158px;" :src="file.attachmentUrl" :previewSrcList="[file.attachmentUrl]" v-if="file.attachmentUrl" fit="contain" />
                        </div>
                        <div v-else-if="file.attachmentUrl" class="file-rect">
                            <ahFileIcon ext="pdf" :url="file.attachmentUrl" />
                        </div>
                        <div v-else class="no-file-rect">
                            <span style="color: #aaa; cursor: default;">未上傳</span>
                        </div>
                    </div>
                    <div class="accounting-detail-items" style="margin-top: 25px;">
                        <div v-if="file.foO_DAT" class="item">
                            <div class="label">單據日期</div>
                            <div class="value">
                                <span class="fw-b">{{ file.foO_DAT | dateFormater("YYYY-MM-dd") }}</span>
                            </div>
                        </div>
                        <div v-if="file.nam" class="item">
                            <div class="label">文件名稱</div>
                            <div class="value">
                                <span>{{ file.nam }}</span>
                            </div>
                        </div>
                        <div v-if="file.note" class="item">
                            <div class="label">備註</div>
                            <div class="value">
                                <span>{{ file.note }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <VariableDetailDialog @close="handleVariableDetailDialogClose" :visible="variableDetailDialog.visible" :sno="variableDetailDialog.sno" />
    </div>
</template>

<script>
import ahFileIcon from '@/components/ui/ahFileIcon.vue';

export default {
    props: {
        queryType: {
            type: Number,
            default: null,
        },
        datas: {
            type: Object,
            default: () => {},
        },
    },
    components: { VariableDetailDialog: () => import('@/components/dashboard/Finance/VariableStatement/DetailDialog.vue'), ahFileIcon },
    data() {
        return {
            variableDetailDialog: {
                visible: false,
                sno: null,
            },
            bankAccountList: [],
        };
    },
    created() {
        this.fetchBankList();
    },
    computed: {
        documentDetail() {
            return this.datas.detail || {};
        },
        isIncome() {
            return this.queryType === 3 || this.queryType === 4;
        },
        isExpense() {
            return this.queryType === 5 || this.queryType === 6;
        },
    },
    methods: {
        async fetchBankList() {
            const resp = await this.$api({
                url: '/Tool/GetBankAccountOptions',
                method: 'GET',
            });

            this.bankAccountList = resp.data.options;
        },
        getHierarchyItemNames(itemNo) {
            const hierarchy = [null, null, null];

            const types = [
                { key: 'payoutSettings', name: '營業支出' },
                { key: 'invoiceSettings', name: '營業收入' },
                { key: 'cInSettings', name: '營業其他收入' },
                { key: 'cInElseSettings', name: '財務資產收入' },
                { key: 'cOutSettings', name: '營業其他支出' },
                { key: 'cOutElseSettings', name: '財務資產支出' },
            ];

            // eslint-disable-next-line no-restricted-syntax, guard-for-in
            for (const t in types) {
                const options = this.$store.getters['statement/statementUiOptions'][types[t].key];
                // eslint-disable-next-line no-restricted-syntax, guard-for-in
                for (const i in options) {
                    const lv1Item = options[i];
                    // eslint-disable-next-line no-restricted-syntax, guard-for-in
                    for (const j in lv1Item.childSettings) {
                        const lv2Item = lv1Item.childSettings[j];
                        if (lv2Item.sno === itemNo) {
                            hierarchy[0] = types[t].name;
                            hierarchy[1] = lv1Item.iteM_NAM;
                            hierarchy[2] = lv2Item.iteM_NAM;
                            break;
                        }
                    }
                }
            }

            return hierarchy.join(' / ');
        },
        getBankName(sno) {
            if (this.bankAccountList.length > 0 && sno) {
                const bank = this.bankAccountList.find((x) => x.sno === sno);
                if (bank) {
                    return bank.tpc;
                }
                return null;
            }
            return null;
        },
        openVariableDetail(sno) {
            this.variableDetailDialog.sno = sno;
            this.variableDetailDialog.visible = true;
        },
        handleVariableDetailDialogClose(refresh) {
            this.$emit('detailClose', refresh);
            this.variableDetailDialog.visible = false;
            this.variableDetailDialog.sno = null;
        },
        getFileExtByUrl(url) {
            return url
                .split('.')
                .pop()
                .toLowerCase();
        },
    },
};
</script>

<style lang="scss"></style>
